import type { Item } from "@/features/space-designer/lib/types";
import { applyPalette } from "@/features/space-designer/lib/svg-sanitize";
import { useMemo } from "react";
import { getDeskPath } from "@/features/space-designer/lib/workstation-layout";

export function ItemShape({ item }: { item: Item }) {
  const { shape, w, h, color, seats } = item;
  const stroke = "var(--furniture-stroke)";
  const fill = color ?? "var(--furniture-fill)";

  switch (shape) {
    case "svg": {
      if (!item.svg) return <rect width={w} height={h} fill={fill} stroke={stroke} />;
      return <CustomSvg item={item} />;
    }
    case "desk-rect": {
      // Exploded workstation desk. If we have origin metadata, draw the same
      // desk geometry (L-shaped, linear, etc.) as the original workstation,
      // minus the chairs and the corner zone.
      if (item.origin) {
        return (
          <path
            d={getDeskPath(item.origin.shape, w, h)}
            fill={fill}
            stroke={stroke}
            strokeWidth={1.5}
          />
        );
      }
      return <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={2} />;
    }
    case "zone-hatched": {
      const step = Math.min(w, h) / 5;
      return (
        <g>
          <rect width={w} height={h} fill="oklch(0.88 0.02 250 / 0.6)" stroke={stroke} strokeWidth={1} />
          {Array.from({ length: 5 }).map((_, i) => {
            const t = step * (i + 1);
            return <line key={i} x1={0} y1={t} x2={t} y2={0} stroke={stroke} strokeWidth={0.6} opacity={0.55} />;
          })}
        </g>
      );
    }
    case "rect":
      return <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={2} />;
    case "l-arc-table": {
      // L-shaped table along the top + left walls with a curved (concave) inner corner.
      // The arm depth is fixed at ~60cm and the inner arc radius is ~40cm.
      const D = Math.min(60, Math.min(w, h) * 0.35);
      const R = Math.min(40, Math.min(w - D, h - D) * 0.6);
      const d = `M 0 0 L ${w} 0 L ${w} ${D} L ${D + R} ${D} A ${R} ${R} 0 0 0 ${D} ${D + R} L ${D} ${h} L 0 ${h} Z`;
      return (
        <g>
          <path d={d} fill={fill} stroke={stroke} strokeWidth={1.5} />
          {/* subtle inner edge highlight along the arc */}
          <path
            d={`M ${D + R} ${D} A ${R} ${R} 0 0 0 ${D} ${D + R}`}
            fill="none"
            stroke={stroke}
            strokeWidth={0.8}
            opacity={0.5}
          />
        </g>
      );
    }
    case "l-curv-table": {
      // L-shaped table with rounded outer corners and a rounded (concave) inner corner.
      // Arms sit against the top and left walls; depth D and inner radius R scale with size.
      const D = Math.min(60, Math.min(w, h) * 0.35);
      const R = Math.min(24, D * 0.45, Math.min(w - D, h - D) * 0.35);
      const d = `M ${R} 0 H ${w - R} Q ${w} 0 ${w} ${R} V ${D} H ${D + R} Q ${D} ${D} ${D} ${D + R} V ${h - R} Q ${D} ${h} ${R} ${h} H 0 V ${R} Q 0 0 ${R} 0 Z`;
      return (
        <g>
          <path d={d} fill={fill} stroke={stroke} strokeWidth={1.5} />
          {/* Inner corner edge highlight */}
          <path
            d={`M ${D + R} ${D} Q ${D} ${D} ${D} ${D + R}`}
            fill="none"
            stroke={stroke}
            strokeWidth={0.8}
            opacity={0.5}
          />
        </g>
      );
    }
    case "circle":
    case "chair":
      return (
        <g>
          <rect width={w} height={h * 0.8} y={h * 0.2} fill={fill} stroke={stroke} strokeWidth={1.5} rx={6} />
          <rect width={w} height={h * 0.2} fill={stroke} opacity={0.7} rx={2} />
        </g>
      );
    case "sofa":
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={10} />
          <rect width={w * 0.92} height={h * 0.55} x={w * 0.04} y={h * 0.35} fill="oklch(0.92 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
          <rect width={w * 0.06} height={h} fill={stroke} opacity={0.25} />
          <rect width={w * 0.06} height={h} x={w * 0.94} fill={stroke} opacity={0.25} />
        </g>
      );
    case "desk":
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={2} />
          <rect x={w * 0.05} y={h * 0.6} width={w * 0.35} height={h * 0.35} fill="none" stroke={stroke} strokeWidth={1} opacity={0.4} />
        </g>
      );
    case "conference": {
      const n = seats ?? 6;
      const perSide = Math.max(1, Math.floor((n - 2) / 2));
      const chairs: { x: number; y: number; rot: number }[] = [];
      for (let i = 0; i < perSide; i++) {
        const x = (w * (i + 1)) / (perSide + 1);
        chairs.push({ x, y: -25, rot: 0 });
        chairs.push({ x, y: h + 25, rot: 180 });
      }
      chairs.push({ x: -25, y: h / 2, rot: 270 });
      chairs.push({ x: w + 25, y: h / 2, rot: 90 });
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={6} />
          {chairs.slice(0, n).map((c, i) => (
            <g key={i} transform={`translate(${c.x - 18}, ${c.y - 18})`}>
              <rect width={36} height={36} fill="oklch(0.96 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
            </g>
          ))}
        </g>
      );
    }
    case "workstation": {
      // Two columns of desks back-to-back, chairs on outer sides.
      // `seats` = total seats (even). Each row = one pair of seats facing each other.
      const n = Math.max(2, seats ?? 12);
      const pairs = Math.max(1, Math.round(n / 2));
      const chairW = Math.min(50, w * 0.22);
      const deskBlockW = w - chairW * 2;
      const rowH = h / pairs;
      const seatPad = Math.min(6, rowH * 0.1);
      return (
        <g>
          {/* Desk block */}
          <rect x={chairW} y={0} width={deskBlockW} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={2} />
          {/* center spine */}
          <line x1={chairW + deskBlockW / 2} y1={0} x2={chairW + deskBlockW / 2} y2={h} stroke={stroke} strokeWidth={1} opacity={0.6} />
          {/* row dividers */}
          {Array.from({ length: pairs - 1 }).map((_, i) => (
            <line key={`r${i}`} x1={chairW} y1={rowH * (i + 1)} x2={chairW + deskBlockW} y2={rowH * (i + 1)} stroke={stroke} strokeWidth={0.8} opacity={0.4} />
          ))}
          {/* chairs */}
          {Array.from({ length: pairs }).map((_, i) => {
            const cy = rowH * i + seatPad;
            const ch = rowH - seatPad * 2;
            return (
              <g key={`c${i}`}>
                <rect x={2} y={cy} width={chairW - 4} height={ch} fill="oklch(0.96 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
                <rect x={chairW - 6} y={cy} width={4} height={ch} fill={stroke} opacity={0.5} rx={1} />
                <rect x={w - chairW + 2} y={cy} width={chairW - 4} height={ch} fill="oklch(0.96 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
                <rect x={w - chairW + 2} y={cy} width={4} height={ch} fill={stroke} opacity={0.5} rx={1} />
              </g>
            );
          })}
        </g>
      );
    }
    case "workstation-linear": {
      // Wall-facing: a single strip of desk along the top (the "wall" side),
      // chairs lined up below, one chair per grid row.
      const n = Math.max(1, seats ?? 5);
      const deskDepth = Math.min(60, w * 0.55);
      const chairW = w - deskDepth;
      const rowH = h / n;
      const seatPad = Math.min(6, rowH * 0.1);
      return (
        <g>
          {/* Wall indicator */}
          <line x1={0} y1={0} x2={w} y2={0} stroke={stroke} strokeWidth={2.5} opacity={0.7} />
          {/* Desk strip along the wall (left side, depth = deskDepth) */}
          <rect x={0} y={0} width={deskDepth} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={2} />
          {/* Row dividers on the desk */}
          {Array.from({ length: n - 1 }).map((_, i) => (
            <line key={`r${i}`} x1={0} y1={rowH * (i + 1)} x2={deskDepth} y2={rowH * (i + 1)} stroke={stroke} strokeWidth={0.8} opacity={0.4} />
          ))}
          {/* Chairs on the open side */}
          {Array.from({ length: n }).map((_, i) => {
            const cy = rowH * i + seatPad;
            const ch = rowH - seatPad * 2;
            return (
              <g key={`c${i}`}>
                <rect x={deskDepth + 2} y={cy} width={chairW - 4} height={ch} fill="oklch(0.96 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
                <rect x={w - 4} y={cy} width={4} height={ch} fill={stroke} opacity={0.5} rx={1} />
              </g>
            );
          })}
        </g>
      );
    }
    case "workstation-l": {
      // L-shaped desk against two walls (top + left). Seats are split evenly between
      // the two arms; the corner square is unoccupiable and hatched.
      const total = Math.max(2, seats ?? 6);
      const perArm = Math.max(1, Math.floor(total / 2));
      const seatW = Math.min(60, w / (perArm + 1), h / (perArm + 1));
      const depth = Math.min(60, Math.min(w, h) * 0.3);
      return (
        <g>
          {/* Wall indicators (top + left) */}
          <line x1={0} y1={0} x2={w} y2={0} stroke={stroke} strokeWidth={2.5} opacity={0.7} />
          <line x1={0} y1={0} x2={0} y2={h} stroke={stroke} strokeWidth={2.5} opacity={0.7} />
          {/* L-shaped desk */}
          <path
            d={`M 0 0 L ${w} 0 L ${w} ${depth} L ${depth} ${depth} L ${depth} ${h} L 0 ${h} Z`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1.5}
          />
          {/* Corner zone — unoccupiable (hatched) */}
          <rect x={0} y={0} width={depth} height={depth} fill="oklch(0.88 0.02 250 / 0.6)" stroke={stroke} strokeWidth={1} />
          {Array.from({ length: 5 }).map((_, i) => {
            const t = (depth / 5) * (i + 1);
            return <line key={i} x1={0} y1={t} x2={t} y2={0} stroke={stroke} strokeWidth={0.6} opacity={0.55} />;
          })}
          {/* Horizontal arm chairs (face inward / south) */}
          {Array.from({ length: perArm }).map((_, i) => {
            const cx = depth + i * seatW + seatW * 0.1;
            const cy = depth + 2;
            const cw = seatW * 0.8;
            const ch = Math.max(10, h - depth - 4);
            return (
              <g key={`h${i}`}>
                <rect x={cx} y={cy} width={cw} height={ch} fill="oklch(0.96 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
                <rect x={cx} y={cy + ch - 4} width={cw} height={4} fill={stroke} opacity={0.5} rx={1} />
              </g>
            );
          })}
          {/* Vertical arm chairs (face inward / east) */}
          {Array.from({ length: perArm }).map((_, i) => {
            const cx = depth + 2;
            const cy = depth + i * seatW + seatW * 0.1;
            const cw = Math.max(10, w - depth - 4);
            const ch = seatW * 0.8;
            return (
              <g key={`v${i}`}>
                <rect x={cx} y={cy} width={cw} height={ch} fill="oklch(0.96 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
                <rect x={cx + cw - 4} y={cy} width={4} height={ch} fill={stroke} opacity={0.5} rx={1} />
              </g>
            );
          })}
        </g>
      );
    }
    case "workstation-corner": {
      // L-shaped desk against two walls (top + left). The corner area is
      // marked as unoccupiable (hatched) — one chair sits on the diagonal.
      const depth = Math.min(60, Math.min(w, h) * 0.4);
      const chairSize = Math.min(50, Math.min(w, h) * 0.28);
      const cx = (depth + w) / 2 - chairSize / 2;
      const cy = (depth + h) / 2 - chairSize / 2;
      return (
        <g>
          <line x1={0} y1={0} x2={w} y2={0} stroke={stroke} strokeWidth={2.5} opacity={0.7} />
          <line x1={0} y1={0} x2={0} y2={h} stroke={stroke} strokeWidth={2.5} opacity={0.7} />
          <path
            d={`M 0 0 L ${w} 0 L ${w} ${depth} L ${depth} ${depth} L ${depth} ${h} L 0 ${h} Z`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1.5}
          />
          <rect x={0} y={0} width={depth} height={depth} fill="oklch(0.88 0.02 250 / 0.6)" stroke={stroke} strokeWidth={1} />
          {Array.from({ length: 5 }).map((_, i) => {
            const t = (depth / 5) * (i + 1);
            return <line key={i} x1={0} y1={t} x2={t} y2={0} stroke={stroke} strokeWidth={0.6} opacity={0.55} />;
          })}
          <rect x={cx} y={cy} width={chairSize} height={chairSize} fill="oklch(0.96 0.01 250)" stroke={stroke} strokeWidth={1} rx={6} />
          <rect x={cx} y={cy} width={chairSize} height={4} fill={stroke} opacity={0.5} rx={1} />
        </g>
      );
    }
    case "workstation-table": {
      // Table-only workstation (no chairs). `seats` = total seats across
      // 2 rows (back-to-back). Columns = seats / 2.
      const n = Math.max(2, seats ?? 10);
      const rows = 2;
      const cols = Math.max(1, Math.round(n / rows));
      const colW = w / cols;
      const rowH = h / rows;
      const notch = Math.min(8, Math.min(colW, rowH) * 0.18);
      return (
        <g>
          {/* Table top */}
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={2} />
          {/* Center horizontal spine (back-to-back) */}
          <line x1={0} y1={rowH} x2={w} y2={rowH} stroke={stroke} strokeWidth={1.2} opacity={0.7} />
          {/* Column dividers between seats */}
          {Array.from({ length: cols - 1 }).map((_, i) => (
            <line
              key={`c${i}`}
              x1={colW * (i + 1)}
              y1={0}
              x2={colW * (i + 1)}
              y2={h}
              stroke={stroke}
              strokeWidth={0.8}
              opacity={0.5}
            />
          ))}
          {/* End notches (cable trays / privacy screens) */}
          <rect x={-notch / 2} y={rowH - notch} width={notch} height={notch * 2} fill={fill} stroke={stroke} strokeWidth={1} />
          <rect x={w - notch / 2} y={rowH - notch} width={notch} height={notch * 2} fill={fill} stroke={stroke} strokeWidth={1} />
        </g>
      );
    }
    case "door": {
      const sweep = item.flipArc ? 0 : 1;
      const endY = item.flipArc ? h / 2 + w : h / 2 - w;
      const arcPath = `M 0 ${h / 2} A ${w} ${w} 0 0 ${sweep} ${w} ${endY}`;
      return (
        <g>
          <line x1={0} y1={h / 2} x2={w} y2={h / 2} stroke="transparent" strokeWidth={16} pointerEvents="stroke" />
          <path d={arcPath} fill="none" stroke="transparent" strokeWidth={16} pointerEvents="stroke" />
          <line x1={0} y1={h / 2} x2={w} y2={h / 2} stroke={stroke} strokeWidth={2} />
          <path d={arcPath} fill="none" stroke={stroke} strokeWidth={1} strokeDasharray="3 3" />
        </g>
      );
    }
    case "stairs":
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} />
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={i} x1={(w / 8) * (i + 1)} y1={0} x2={(w / 8) * (i + 1)} y2={h} stroke={stroke} strokeWidth={1} />
          ))}
          <path d={`M ${w * 0.1} ${h / 2} L ${w * 0.9} ${h / 2}`} stroke={stroke} strokeWidth={1.5} markerEnd="url(#arrow)" />
        </g>
      );
    case "lift":
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} />
          <line x1={w / 2} y1={0} x2={w / 2} y2={h} stroke={stroke} strokeWidth={1} strokeDasharray="4 4" />
          <path d={`M ${w * 0.4} ${h * 0.35} L ${w * 0.5} ${h * 0.25} L ${w * 0.6} ${h * 0.35}`} fill="none" stroke={stroke} strokeWidth={1.5} />
          <path d={`M ${w * 0.4} ${h * 0.65} L ${w * 0.5} ${h * 0.75} L ${w * 0.6} ${h * 0.65}`} fill="none" stroke={stroke} strokeWidth={1.5} />
        </g>
      );
    case "sink":
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={4} />
          <rect x={w * 0.1} y={h * 0.2} width={w * 0.8} height={h * 0.65} fill="oklch(0.94 0.01 220)" stroke={stroke} strokeWidth={1} rx={3} />
          <circle cx={w / 2} cy={h * 0.15} r={2} fill={stroke} />
        </g>
      );
    case "toilet":
      return (
        <g>
          <rect width={w} height={h * 0.35} fill={fill} stroke={stroke} strokeWidth={1.5} rx={3} />
          <ellipse cx={w / 2} cy={h * 0.7} rx={w * 0.45} ry={h * 0.3} fill={fill} stroke={stroke} strokeWidth={1.5} />
        </g>
      );
    case "urinal": {
      // Wall-hung urinal — top-down D-shaped footprint with a flush panel on
      // the wall side (top) and a rounded bowl at the front.
      const r = w / 2;
      const vertical = Math.max(0, h - r);
      const flushW = w * 0.35;
      return (
        <g>
          <path
            d={`M 0 0 H ${w} V ${vertical} A ${r} ${r} 0 0 1 0 ${vertical} Z`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1.5}
          />
          {/* Flush panel / sensor strip at the wall side */}
          <rect
            x={(w - flushW) / 2}
            y={2}
            width={flushW}
            height={Math.max(4, vertical * 0.25)}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
            opacity={0.7}
            rx={2}
          />
          {/* Drain / bowl hint */}
          <circle
            cx={w / 2}
            cy={Math.min(h - r * 0.4, vertical + r)}
            r={Math.min(4, r * 0.22)}
            fill={stroke}
            opacity={0.4}
          />
        </g>
      );
    }
    case "tv":
      return (
        <g>
          <rect width={w} height={h} fill="oklch(0.25 0.02 250)" stroke={stroke} strokeWidth={1.5} rx={2} />
        </g>
      );
    case "projector": {
      // Ceiling-mounted projector: housing + lens + light cone hint.
      const cx = w / 2;
      const cy = h / 2;
      const lensR = Math.min(w, h) * 0.22;
      return (
        <g>
          <rect width={w} height={h} fill="oklch(0.35 0.01 250)" stroke={stroke} strokeWidth={1.5} rx={3} />
          <circle cx={cx} cy={cy} r={lensR} fill="oklch(0.85 0.03 220)" stroke={stroke} strokeWidth={1} />
          <circle cx={cx} cy={cy} r={lensR * 0.5} fill="oklch(0.2 0.02 240)" />
          <rect x={w * 0.1} y={h * 0.1} width={w * 0.12} height={h * 0.12} fill="oklch(0.6 0.15 30)" opacity={0.8} />
        </g>
      );
    }
    case "conference-room": {
      // Boardroom-style table with rounded ends, central cable-management
      // spine, and a subtle inset. No integrated seats — chairs are added
      // separately so layouts stay flexible.
      const rx = h / 2;
      return (
        <g>
          {/* Table top with rounded ends */}
          <rect width={w} height={h} rx={rx} ry={rx} fill={fill} stroke={stroke} strokeWidth={1.5} />
          {/* Inset / cable tray */}
          <rect
            x={rx * 0.4}
            y={h * 0.32}
            width={w - rx * 0.8}
            height={h * 0.36}
            rx={h * 0.18}
            ry={h * 0.18}
            fill="none"
            stroke={stroke}
            strokeWidth={1}
            opacity={0.45}
          />
          {/* Central cable spine */}
          <line x1={rx} y1={h / 2} x2={w - rx} y2={h / 2} stroke={stroke} strokeWidth={0.8} opacity={0.5} strokeDasharray="4 3" />
        </g>
      );
    }
    case "mic":
      return (
        <g>
          <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2} fill={fill} stroke={stroke} strokeWidth={1.5} />
          <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 4} fill={stroke} opacity={0.6} />
        </g>
      );
    case "plant":
      return (
        <g>
          <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2} fill="oklch(0.7 0.12 145)" stroke={stroke} strokeWidth={1.5} />
          <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3.5} fill="oklch(0.55 0.14 145)" />
        </g>
      );
    case "fridge": {
      // Top-down refrigerator footprint: a rectangular box with a door seam
      // and a small handle to read as a floor-plan appliance, not a front elevation.
      const inset = Math.min(6, w * 0.1, h * 0.1);
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={4} />
          {/* Door seam (freezer on top / fridge below) */}
          <line x1={inset} y1={h * 0.35} x2={w - inset} y2={h * 0.35} stroke={stroke} strokeWidth={1} opacity={0.7} />
          {/* Door handle */}
          <rect x={w * 0.72} y={h * 0.18} width={3} height={h * 0.12} fill={stroke} rx={1} />
          <rect x={w * 0.72} y={h * 0.58} width={3} height={h * 0.18} fill={stroke} rx={1} />
        </g>
      );
    }
    case "microwave": {
      // Countertop microwave — top-down footprint with a small control strip
      // at the front/right edge so it reads from plan view, not elevation.
      const pad = Math.min(4, w * 0.08);
      const panelW = w * 0.28;
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={3} />
          {/* Door area */}
          <rect
            x={pad}
            y={pad}
            width={w - panelW - pad * 2}
            height={h - pad * 2}
            fill="oklch(0.92 0.01 250)"
            stroke={stroke}
            strokeWidth={1}
            rx={2}
          />
          {/* Control strip (front/right side) */}
          <rect
            x={w - panelW + pad / 2}
            y={pad}
            width={panelW - pad}
            height={h - pad * 2}
            fill="oklch(0.88 0.01 250)"
            stroke={stroke}
            strokeWidth={1}
            rx={2}
          />
          {/* Buttons / display */}
          <rect x={w - panelW + pad * 1.5} y={h * 0.25} width={panelW - pad * 3} height={h * 0.18} fill={stroke} opacity={0.5} rx={1} />
        </g>
      );
    }
    case "oven": {
      // Built-in oven — top-down footprint: a glass door panel and a control
      // strip across the top edge.
      const pad = Math.min(4, w * 0.08);
      const panelH = h * 0.22;
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} rx={3} />
          {/* Control strip at the front/top */}
          <rect
            x={pad}
            y={pad}
            width={w - pad * 2}
            height={panelH - pad}
            fill="oklch(0.88 0.01 250)"
            stroke={stroke}
            strokeWidth={1}
            rx={2}
          />
          {/* Glass door */}
          <rect
            x={pad}
            y={panelH}
            width={w - pad * 2}
            height={h - panelH - pad}
            fill="oklch(0.92 0.01 250)"
            stroke={stroke}
            strokeWidth={1}
            rx={2}
          />
          {/* Knobs */}
          <circle cx={w * 0.25} cy={panelH / 2} r={Math.min(3, w * 0.04)} fill={stroke} />
          <circle cx={w * 0.45} cy={panelH / 2} r={Math.min(3, w * 0.04)} fill={stroke} />
          <circle cx={w * 0.65} cy={panelH / 2} r={Math.min(3, w * 0.04)} fill={stroke} />
        </g>
      );
    }
    case "ro-dispenser": {
      // Top-down water dispenser: circular footprint with central tap, drip tray
      // and a small control strip along the front/right arc.
      const cx = w / 2;
      const cy = h / 2;
      const r = Math.min(w, h) / 2;
      const trayR = r * 0.55;
      const tapR = r * 0.18;
      return (
        <g>
          <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={1.5} />
          {/* Drip tray */}
          <circle cx={cx} cy={cy} r={trayR} fill="oklch(0.92 0.01 250)" stroke={stroke} strokeWidth={1} />
          {/* Tap spout */}
          <circle cx={cx} cy={cy} r={tapR} fill="oklch(0.85 0.02 220)" stroke={stroke} strokeWidth={1} />
          <circle cx={cx} cy={cy} r={tapR * 0.45} fill={stroke} opacity={0.5} />
          {/* Control buttons on the front/right quadrant */}
          <rect x={cx + r * 0.25} y={cy - r * 0.1} width={r * 0.25} height={r * 0.2} rx={2} fill="oklch(0.88 0.01 250)" stroke={stroke} strokeWidth={0.6} />
        </g>
      );
    }
    case "shelf":
      return (
        <g>
          <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} />
          <line x1={0} y1={h / 2} x2={w} y2={h / 2} stroke={stroke} strokeWidth={0.8} opacity={0.6} />
          {Array.from({ length: 5 }).map((_, i) => (
            <line key={i} x1={(w / 5) * (i + 1)} y1={0} x2={(w / 5) * (i + 1)} y2={h} stroke={stroke} strokeWidth={0.6} opacity={0.4} />
          ))}
        </g>
      );
    case "pillar-square":
      return <rect width={w} height={h} fill="oklch(0.45 0.02 250)" stroke={stroke} strokeWidth={1.5} />;
    case "pillar-round":
      return <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} fill="oklch(0.45 0.02 250)" stroke={stroke} strokeWidth={1.5} />;
    case "entry":
      return (
        <g>
          <rect width={w} height={h} fill="none" stroke={stroke} strokeWidth={1.5} strokeDasharray="6 4" />
          <path d={`M ${w * 0.3} ${h / 2} L ${w * 0.7} ${h / 2}`} stroke={stroke} strokeWidth={2} markerEnd="url(#arrow)" />
        </g>
      );
    default:
      return <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={1.5} />;
  }
}

function CustomSvg({ item }: { item: Item }) {
  const { w, h, svg } = item;
  const inner = useMemo(() => applyPalette(svg!.inner, svg!.overrides ?? {}), [svg]);
  return (
    <svg
      x={0}
      y={0}
      width={w}
      height={h}
      viewBox={svg!.viewBox}
      preserveAspectRatio="xMidYMid meet"
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}